// AuthContext.jsx
import React, { createContext, useState, useEffect } from "react";
import client from "../api/client"

/**
 * AuthContext
 *
 * Exposes:
 * - user: { name?, mobile?, email?, icsid?, apikey?, apikeyExpiry }
 * - isLoggedIn: boolean
 * - loading: boolean
 * - sendOtp(contact) -> { ok, message }
 * - verifyOtp(contact, otp) -> { ok, message, user? }
 * - logout()
 * - refreshDonorData() -> refetches DONDATA for current apikey (best-effort)
 * - isSessionValid() -> boolean
 *
 * Notes:
 * - contact can be phone (10 digits) or email.
 * - VEROTP returns per-user apikey (approx 30 min validity per doc).
 * - DONDATA mapping uses many fallback keys to be robust to provider shapes.
 */

export const AuthContext = createContext();

const PROVIDER_BASE = "https://srv.konsoftech.in/ics/api/ah";

function providerUrl(params) {
  return PROVIDER_BASE + "?" + new URLSearchParams(params).toString();
}

async function callProvider(params) {
  const url = providerUrl(params);
  const res = await fetch(url);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Network error ${res.status} ${text}`);
  }
  const json = await res.json();
  return json;
}

/** Normalize donor object shape returned by DONDATA */
function normalizeDonor(d) {
  if (!d || typeof d !== "object") return null;
  return {
    icsid: d.icsid || d.id || d.contactId || null,
    name: d.legalName || d.donorName || d.name || d.fullName || null,
    email: d.email || d.donorEmail || d.emailId || null,
    // many providers use donorContact, phone, contact, mobile, msisdn etc.
    mobile:
      (d.donorContact || d.phone || d.contact || d.mobile || d.msisdn || "")
        .toString()
        .replace(/\D/g, "") || null,
    pan: d.panNo || d.pan || null,
    address: (d.addrl1 || d.addrLine1 || d.addr1 || d.donorAddress || d.address) || null,
    city: d.city || null,
    state: d.state || null,
    country: d.country || d.donorCountry || null,
    pin: d.pincode || d.pin || d.donorPin || null,
    donations: Array.isArray(d.donations) ? d.donations : d.don || d.donationList || null,
    raw: d,
  };
}

/** Fetch DONDATA using an apikey; returns array of normalized donors */
async function fetchDondata(apikey) {
  if (!apikey) throw new Error("apikey required for DONDATA");
  const json = await callProvider({ oper: "DONDATA", apikey });
  if (!json) throw new Error("Empty response from DONDATA");
  if (json.status !== "OK") throw new Error(json.message || "DONDATA failed");
  const data = json.data || [];
  return data.map(normalizeDonor);
}


export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState({}); // { apikey, apikeyExpiry, mobile, name, email, icsid, ... }
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  // Restore auth from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem("iskcon_auth");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.apikey || !parsed.apikeyExpiry) return;
      if (Date.now() > parsed.apikeyExpiry) {
        localStorage.removeItem("iskcon_auth");
        return;
      }
      setUser(parsed);
      setIsLoggedIn(true);
    } catch (e) {
      console.warn("Auth restore error", e);
    }
  }, []);

  const saveUser = (u) => {
    setUser(u);
    setIsLoggedIn(!!u);
    if (u) localStorage.setItem("iskcon_auth", JSON.stringify(u));
    else localStorage.removeItem("iskcon_auth");
  };

  // Helper to detect if contact is email or phone
  const contactKey = (contact = "") => {
    if (!contact) return "phone";
    if (contact.includes("@")) return "email";
    // numeric phone detection fallback
    const digits = (contact + "").replace(/\D/g, "");
    return digits.length >= 6 ? "phone" : "email";
  };

  // Public helper to check session validity
  const isSessionValid = () => {
    return !!user && !!user.apikey && !!user.apikeyExpiry && Date.now() < user.apikeyExpiry;
  };

  // Send OTP (GENOTP)
  const sendOtp = async (contact) => {
    try {
      setLoading(true);
      const res = await client.get(`/gen-otp/${contact}`);
      if (res.data.status === "OK") {
        return { ok: true, message: res.data.message };
      }
      return { ok: false, message: res.data.message };
    } catch (err) {
      return { ok: false, message: err.message };
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (contact, otp) => {
    try {
      setLoading(true);
      const res = await client.get(`/ver-otp/${contact}/${otp}`);
      const data = res.data;
      if (data.status === "OK" && data.apikey) {
        const apikeyExpiry = Date.now() + 30 * 60 * 1000;
        const userObj = {
          apikey: data.apikey,
          apikeyExpiry,
          mobile: contact,
          icsid: data.icsid,
          name: null,
          email: null,
          ...data,
        };
        saveUser(userObj);
        return { ok: true, message: data.message, user: userObj };
      }
      return { ok: false, message: data.message };
    } catch (err) {
      return { ok: false, message: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Refresh donor data using stored apikey (best-effort)
  const refreshDonorData = async () => {
    if (!user || !user.apikey) return null;
    try {
      setLoading(true);
      const donors = await fetchDondata(user.apikey);
      if (Array.isArray(donors) && donors.length) {
        const donor = donors[0];
        const updated = {
          ...user,
          name: donor.name || user.name,
          email: donor.email || user.email,
          mobile: donor.mobile || user.mobile,
          icsid: donor.icsid || user.icsid,
        };
        saveUser(updated);
        return updated;
      }
      return null;
    } catch (err) {
      console.warn("refreshDonorData failed", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    saveUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoggedIn,
        loading,
        sendOtp,
        verifyOtp,
        refreshDonorData,
        logout,
        isSessionValid,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

